import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../../assets/logo.jpg";
import {
  Search,
  Stethoscope,
  FlaskConical,
  Heart,
  UserRound,
  Sparkles,
  Baby,
  Ear,
  Brain,
  ShieldCheck,
  PackageCheck,
  Star,
  Clock3,
  X,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/website` 
  : "https://test.avistha.com/api/website";

const SPECIALTIES = [
  { label: "Cardiology", icon: Heart, bg: "bg-rose-50", ring: "ring-rose-100" },
  { label: "GP", icon: UserRound, bg: "bg-sky-50", ring: "ring-sky-100" },
  { label: "Dermatology", icon: Sparkles, bg: "bg-emerald-50", ring: "ring-emerald-100" },
  { label: "Gynecology", icon: Baby, bg: "bg-cyan-50", ring: "ring-cyan-100" },
  { label: "ENT", icon: Ear, bg: "bg-amber-50", ring: "ring-amber-100" },
  { label: "Neurology", icon: Brain, bg: "bg-indigo-50", ring: "ring-indigo-100" },
];

// 🌟 Helper for text highlighting
const HighlightText = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-emerald-100 font-bold">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
};

// 🌟 Star Rating Component
function StarRow({ rating }) {
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          size={13}
          className={i < full ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"}
        />
      ))}
    </span>
  );
}

// 🌟 Auto-rotating Ad Slideshow Component
function AdSlideshow({ ads }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!ads || ads.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 5000); // Rotates every 5 seconds
    return () => clearInterval(interval);
  }, [ads]);

  if (!ads || ads.length === 0) {
    return (
      <div className="flex h-full min-h-[250px] flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 text-slate-400">
        <Sparkles className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">Your Health, Our Priority</p>
      </div>
    );
  }

  const currentAd = ads[currentIndex];

  return (
    <a
      href={currentAd.link_url || "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="flex h-full min-h-[250px] flex-col justify-center overflow-hidden rounded-3xl border border-amber-100 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 p-8 shadow-sm transition hover:shadow-md"
    >
      <div className="flex flex-col gap-4">
        <div>
          <span className="inline-block rounded bg-amber-500 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
            Featured Campaign
          </span>
        </div>
        <p className="font-display text-2xl font-extrabold text-slate-900 leading-tight md:text-3xl">
          {currentAd.title}
        </p>
        <span className="mt-2 text-sm font-bold text-amber-700 underline underline-offset-4">
          Learn More &rarr;
        </span>
      </div>
    </a>
  );
}

export default function Home() {
  const navigate = useNavigate(); // Hook for navigation
  const [doctors, setDoctors] = useState([]);
  const [labs, setLabs] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);

  const fetchDefaultDoctors = async () => {
    try {
      const res = await fetch(`${API_BASE}/doctors`);
      const payload = await res.json();
      const data = payload.data || payload;
      setDoctors(data.doctors || []);
    } catch (err) {
      console.error("Error loading doctors:", err);
    }
  };

  useEffect(() => {
    const initData = async () => {
      try {
        await Promise.all([
          fetchDefaultDoctors(),
          fetch(`${API_BASE}/ads`)
            .then((res) => res.json())
            .then((payload) => {
              const data = payload.data || payload;
              setAds(data.ads || []);
            }),
        ]);
      } catch (err) {
        console.error("Error loading homepage assets:", err);
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    const controller = new AbortController();
    const delayDebounceTimer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(searchQuery)}`, {
          signal: controller.signal,
        });
        const resPayload = await res.json();
        const data = resPayload.data || resPayload;

        if (data) {
          const docs = data.doctors || [];
          const lbs = data.labs || [];
          const items = [];

          docs.forEach((d) => {
            if (!items.some((i) => i.text === d.name)) {
              items.push({ type: "doctor", text: d.name, sub: d.specialization });
            }
          });

          lbs.forEach((l) => {
            if (!items.some((i) => i.text === l.name)) {
              items.push({ type: "lab", text: l.name, sub: "Laboratory Diagnostics" });
            }
          });
          setSuggestions(items.slice(0, 7));
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Auto-suggestion parsing error:", err);
        }
      }
    }, 300);

    return () => {
      clearTimeout(delayDebounceTimer);
      controller.abort();
    };
  }, [searchQuery]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) handleSuggestionSelect(suggestions[activeIndex].text);
      else executeSearch(searchQuery);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const executeSearch = async (targetQuery) => {
    if (!targetQuery.trim()) {
      handleClearSearch();
      return;
    }

    setSuggestions([]);
    setShowSuggestions(false);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/search?q=${encodeURIComponent(targetQuery)}`);
      if (!res.ok) throw new Error("Server response failed");
      const resPayload = await res.json();
      const data = resPayload.data || resPayload;

      setDoctors(Array.isArray(data.doctors) ? data.doctors : []);
      setLabs(Array.isArray(data.labs) ? data.labs : []);

      if ((!data.doctors || data.doctors.length === 0) && (!data.labs || data.labs.length === 0)) {
        setError("No results found.");
      }
    } catch (err) {
      console.error("Search pipeline failure:", err);
      setError("Unable to connect to search service.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    executeSearch(searchQuery);
  };

  const handleSpecialtyClick = (specialtyLabel) => {
    setSearchQuery(specialtyLabel);
    executeSearch(specialtyLabel);
  };

  const handleSuggestionSelect = (itemText) => {
    setSearchQuery(itemText);
    executeSearch(itemText);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSuggestions([]);
    setLabs([]);
    setLoading(true);
    fetchDefaultDoctors().finally(() => setLoading(false));
  };

  const sidebarAds = ads.filter((ad) => ad.position === "sidebar");
  const bannerAds = ads.filter((ad) => ad.position === "banner");

  if (loading && doctors.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800">
      
      {/* 🌟 Navigation Bar */}
      

      {/* 🌟 Hero Section */}
      <section className="bg-emerald-50/70 px-6 py-14">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
          
          {/* LEFT SIDE: Content, Search, and Action Cards */}
          <div className="flex flex-col gap-8">
            <div>
             
              <p className="mt-4 text-lg text-slate-600">Find Doctors and Tests near you</p>
            </div>

            <form
              onSubmit={handleSearchSubmit}
              ref={dropdownRef}
              className="relative flex w-full max-w-xl rounded-2xl border border-emerald-100 bg-white shadow-sm"
            >
              <div className="flex flex-1 items-center gap-3 px-4 rounded-l-2xl">
                <Search className="h-5 w-5 shrink-0 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search doctors, specializations or laboratories..."
                  className="w-full py-4 pr-6 text-sm text-slate-700 outline-none placeholder:text-slate-400"
                  value={searchQuery}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                    setActiveIndex(-1);
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-16 p-1 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="flex items-center justify-center bg-emerald-600 px-6 text-white transition hover:bg-emerald-700 rounded-r-2xl"
              >
                <Search className="h-5 w-5" />
              </button>

              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-[105%] left-0 right-0 z-50 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl max-h-64 overflow-y-auto">
                  {suggestions.map((item, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionSelect(item.text)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${
                        index === activeIndex ? "bg-slate-100" : "hover:bg-slate-50"
                      }`}
                    >
                      {item.type === "doctor" && <UserRound className="h-4 w-4 text-emerald-600 shrink-0" />}
                      {item.type === "specialty" && <Sparkles className="h-4 w-4 text-indigo-500 shrink-0" />}
                      {item.type === "lab" && <FlaskConical className="h-4 w-4 text-teal-600 shrink-0" />}

                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-800">
                          <HighlightText text={item.text} query={searchQuery} />
                        </span>
                        <span className="text-[11px] text-slate-400 leading-none mt-0.5">{item.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </form>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 max-w-xl">
              <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-slate-100">
                <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <Stethoscope className="h-8 w-8 text-emerald-600" />
                </div>
                <p className="font-display font-semibold text-slate-900">Book Doctor</p>
                <button onClick={() => navigate("/login/patient")} className="mt-4 w-full rounded-full bg-emerald-600 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700">
                  Book Appointment
                </button>
              </div>

              <div className="flex flex-col items-center rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-slate-100">
                <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <FlaskConical className="h-8 w-8 text-emerald-600" />
                </div>
                <p className="font-display font-semibold text-slate-900">Book Lab Test</p>
                <button onClick={() => navigate("/login/patient")} className="mt-4 w-full rounded-full bg-emerald-600 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700">
                  Book Test
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Interactive Advertisement Slideshow */}
          <div className="w-full h-full">
            <AdSlideshow ads={bannerAds} />
          </div>

        </div>
      </section>

      {/* 🌟 Main Content Area */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 py-10 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          {error && <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-center text-sm">{error}</div>}

          {labs.length > 0 && (
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="font-display text-2xl font-bold text-slate-900">Matched Laboratories</h2>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {labs.map((lab) => (
                  <div
                    key={lab.id}
                    className="flex items-center justify-between border border-slate-100 rounded-2xl p-4 bg-slate-50/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex items-center justify-center rounded-full bg-teal-50 text-teal-600">
                        <FlaskConical className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="font-display font-semibold text-slate-900">{lab.name}</p>
                        <p className="text-[11px] text-slate-400">{lab.email}</p>
                      </div>
                    </div>
                    <button onClick={() => navigate("/login/patient")} className="rounded-full bg-slate-900 px-3 py-1.5 text-[10px] font-semibold text-white">
                      Book Test
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-2xl font-bold text-slate-900">Find Nearby Doctors</h2>
                <p className="mt-1 text-sm text-slate-500">Top Specialists in Kharagpur</p>
              </div>
            </div>

            <div className="mt-5 flex gap-3 overflow-x-auto pb-1">
              {SPECIALTIES.map(({ label, icon: Icon, bg, ring }) => (
                <button
                  key={label}
                  onClick={() => handleSpecialtyClick(label)}
                  className={`flex w-24 shrink-0 flex-col items-center gap-2 rounded-2xl ${bg} ring-1 ${ring} px-3 py-4 transition hover:-translate-y-0.5 hover:shadow-sm`}
                >
                  <Icon className="h-6 w-6 text-slate-700" strokeWidth={1.8} />
                  <span className="text-[11px] font-semibold text-slate-700">{label}</span>
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-4">
              {doctors.length === 0 ? (
                <div className="text-center py-10 border border-dashed border-slate-200 rounded-2xl">
                  <p className="text-sm text-slate-400">No doctors available match your criteria.</p>
                </div>
              ) : (
                doctors.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex flex-col gap-4 rounded-2xl border border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={doc.avatarUrl || "/images/default-doctor.png"}
                        alt={doc.name}
                        className="h-14 w-14 rounded-full object-cover"
                      />
                      <div>
                        <p className="font-display font-semibold text-slate-900">{doc.name}</p>
                        <p className="text-xs text-emerald-700">
                          {doc.specialization} · {doc.degree}
                        </p>
                        <div className="mt-1 flex items-center gap-1.5">
                          <StarRow rating={doc.rating || 4.5} />
                          <span className="text-[11px] text-slate-400">({doc.reviews || 0})</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-sm font-semibold text-slate-500 mb-2">
                         <span className="text-base font-bold text-slate-950"></span>
                      </p>
                      <button onClick={() => navigate("/login/patient")} className="whitespace-nowrap rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700">
                        Book Appointment
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div>
          {sidebarAds.length > 0 && (
            <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
              <h2 className="font-display text-2xl font-bold text-slate-900">Advertisements</h2>
              <div className="mt-5 space-y-4">
                {sidebarAds.map((ad) => (
                  <a
                    key={ad.id}
                    href={ad.link_url || "#"}
                    className="group flex items-center justify-between gap-4 rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/30 p-4 ring-1 ring-slate-100"
                  >
                    <div className="flex-1">
                      <p className="font-display font-semibold text-slate-900">{ad.title}</p>
                      <button className="mt-3 rounded-full bg-slate-900 px-4 py-1.5 text-xs font-semibold text-white">
                        Explore
                      </button>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      
    </div>
  );
}